import { useEffect, useState } from 'react';
import { useAuth } from '@/features/auth';
import { listExtrasMonth, listInvoiceDrafts } from '@/features/planning/api';
import {
  fetchDashboardSummary,
  isoWeek,
  type DashboardSummary,
} from '../api';
import { KpiTile } from './KpiTile';
import { WeekFlowChart } from './WeekFlowChart';
import { SignalsFeed, type Signal } from './SignalsFeed';
import { PortfolioTable } from './PortfolioTable';
import { CrewPanel } from './CrewPanel';
import { TodayRail } from './TodayRail';
import { type Language, type ViewState } from '@/types';

interface Props {
  language: Language;
  onNavigate: (view: ViewState) => void;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatSek(value: number): string {
  if (value >= 1000) {
    return `${Math.round(value / 1000)}k`;
  }
  return `${Math.round(value)}`;
}

function formatSekFull(value: number): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0,
  }).format(value);
}

function currentIsoWeek(): number {
  return isoWeek(new Date());
}

// ── Clock ────────────────────────────────────────────────────────────────────

function LiveClock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const hms = now.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = now.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
  const weekNum = currentIsoWeek();

  return (
    <div className="flex items-center gap-4 shrink-0">
      <div className="flex items-center gap-1.5">
        <div
          className="w-1.5 h-1.5 rounded-full cmd-pulse"
          style={{ background: '#1B7F4D' }}
        />
        <span className="cmd-label text-[#1B7F4D] text-[9px]">LIVE</span>
      </div>
      <span
        className="font-mono tabular-nums text-sm tracking-widest"
        style={{ color: '#073331', letterSpacing: '0.08em' }}
      >
        {hms}
      </span>
      <span className="cmd-label">{dateStr}</span>
      <span
        className="cmd-label px-1.5 py-0.5 rounded"
        style={{ background: 'rgba(160,123,82,0.14)', color: '#8A6A47' }}
      >
        V.{weekNum}
      </span>
    </div>
  );
}

// ── Build signals purely from the summary signal counts ──────────────────────

function buildSignals(s: DashboardSummary['signals']): Signal[] {
  const signals: Signal[] = [];

  if (s.unassigned_7d > 0) {
    signals.push({
      id: 'unassigned',
      severity: 'crit',
      tag: 'OBEMANNAT',
      text: `${s.unassigned_7d} jobb utan tilldelad städare`,
      context: '7D',
      view: 'approvals',
    });
  }

  if (s.sites_missing_points > 0) {
    signals.push({
      id: 'missing-points',
      severity: 'warn',
      tag: 'POÄNGDATA SAKNAS',
      text: `${s.sites_missing_points} objekt saknar poäng (lön+pris)`,
      context: `${s.sites_missing_points} ST`,
      view: 'brfs',
    });
  }

  if (s.sites_no_jobs_30d > 0) {
    signals.push({
      id: 'no-jobs-30d',
      severity: 'warn',
      tag: 'INGA JOBB 30D',
      text: `${s.sites_no_jobs_30d} objekt med pris men inga schemalagda jobb`,
      context: `${s.sites_no_jobs_30d} ST`,
      view: 'brfs',
    });
  }

  if (s.sites_missing_price > 0) {
    signals.push({
      id: 'missing-price',
      severity: 'warn',
      tag: 'PRISDATA SAKNAS',
      text: `${s.sites_missing_price} objekt saknar prissättning`,
      context: `${s.sites_missing_price} st`,
      view: 'brfs',
    });
  }

  if (s.sites_missing_fortnox > 0) {
    signals.push({
      id: 'missing-fortnox',
      severity: 'info',
      tag: 'FORTNOX KUNDNR SAKNAS',
      text: `${s.sites_missing_fortnox} objekt saknar Fortnox-kundnr`,
      context: `${s.sites_missing_fortnox} st`,
      view: 'economy',
    });
  }

  if (s.pending_approvals > 10) {
    signals.push({
      id: 'approval-queue',
      severity: 'warn',
      tag: 'GODKÄNNANDEKÖ',
      text: `${s.pending_approvals} jobb väntar godkännande`,
      context: `${s.pending_approvals}`,
      view: 'approvals',
    });
  }

  if (s.qc_failed_30d > 0) {
    signals.push({
      id: 'qc-failed',
      severity: 'crit',
      tag: 'UK UNDERKÄND',
      text: `${s.qc_failed_30d} kvalitetskontroll${s.qc_failed_30d > 1 ? 'er' : ''} underkänd`,
      context: `${s.qc_failed_30d}`,
      view: 'qc',
    });
  }

  return signals;
}

// ── CommandDashboard ─────────────────────────────────────────────────────────

const copy = {
  sv: {
    opsLabel: 'OPERATIONS / ÖVERSIKT',
    welcome: 'Välkommen tillbaka,',
  },
  en: {
    opsLabel: 'OPERATIONS / OVERVIEW',
    welcome: 'Welcome back,',
  },
} as const;

export function CommandDashboard({ language, onNavigate }: Props) {
  const { profile, session } = useAuth();
  const token = session?.access_token ?? null;
  const text = copy[language];
  const displayName = profile?.full_name ?? 'User';

  // ── State ────────────────────────────────────────────────────────────────
  // One server-aggregated summary drives every panel. The two extra calls are
  // kept because their numbers are computed on the fly by the Worker and are
  // NOT present in the RPC summary:
  //   • listInvoiceDrafts → FAKTURERAT /MÅN (drafts generated per request)
  //   • listExtrasMonth   → EXTRA EJ GODK. (monthly extras rollup)
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [invoicedThisMonthSek, setInvoicedThisMonthSek] = useState<number | null>(null);
  const [pendingExtras, setPendingExtras] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const monthStr = `${year}-${String(month).padStart(2, '0')}`;

    Promise.allSettled([
      fetchDashboardSummary(token),
      listInvoiceDrafts(year, month, token),
      listExtrasMonth({ month: monthStr }, token),
    ]).then(([summaryRes, invoicesRes, extrasRes]) => {
      if (cancelled) return;

      setSummary(summaryRes.status === 'fulfilled' ? summaryRes.value : null);

      setInvoicedThisMonthSek(
        invoicesRes.status === 'fulfilled'
          ? invoicesRes.value
              .filter((d) => d.status !== 'skipped')
              .reduce((sum, d) => sum + (d.total_ex_moms ?? 0), 0)
          : null,
      );

      setPendingExtras(
        extrasRes.status === 'fulfilled'
          ? (extrasRes.value.grandTotals.byStatus['assigned'] ?? 0)
          : null,
      );

      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [token]);

  // ── Derived slices (degrade to skeleton / em-dash when missing) ───────────
  const kpis = summary?.kpis ?? null;
  const portfolio = summary?.portfolio ?? null;
  const signals = summary ? buildSignals(summary.signals) : [];
  const pointsSpark = (summary?.week_flow ?? []).map((w) => ({ value: w.total_points }));

  const emDash = '—';

  return (
    <div className="cmd-canvas min-h-full p-4 pt-16 md:pt-6 md:p-6 space-y-4 animate-fade-in xl:h-full xl:min-h-0 xl:overflow-hidden xl:flex xl:flex-col xl:space-y-0 xl:gap-4">
      {/* ── Command Header ──────────────────────────────────────────────── */}
      <header className="flex items-start justify-between flex-wrap gap-3 xl:shrink-0">
        <div className="space-y-0.5">
          <p className="cmd-label">{text.opsLabel}</p>
          <h1
            className="text-base font-mono font-medium"
            style={{ color: '#2E4640' }}
          >
            {text.welcome}{' '}
            <span style={{ color: '#8A6A47' }}>{displayName}</span>
          </h1>
        </div>
        <LiveClock />
      </header>

      {/* ── KPI Band ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 xl:shrink-0">
        <KpiTile
          label="GODKÄNN VÄNTAR"
          value={kpis ? kpis.pending_approvals : emDash}
          subtext={
            kpis && kpis.pending_approvals > 0
              ? `ÄLDSTA ${kpis.oldest_pending_days} DGR`
              : 'VÄNTAR SIGNATUR'
          }
          loading={loading}
          onClick={() => onNavigate('approvals')}
          tone={
            kpis && kpis.oldest_pending_days > 21
              ? 'crit'
              : kpis && (kpis.oldest_pending_days > 7 || kpis.pending_approvals > 10)
                ? 'warn'
                : 'neutral'
          }
        />
        <KpiTile
          label="EXTRA EJ GODK."
          value={pendingExtras ?? emDash}
          subtext="DENNA MÅNAD"
          loading={loading}
          onClick={() => onNavigate('economy')}
          tone={pendingExtras != null && pendingExtras > 5 ? 'warn' : 'neutral'}
        />
        <KpiTile
          label="FAKTURERAT /MÅN"
          value={invoicedThisMonthSek != null ? formatSek(invoicedThisMonthSek) : emDash}
          subtext={
            invoicedThisMonthSek != null
              ? `${formatSekFull(invoicedThisMonthSek)} EX MOMS`
              : '—'
          }
          loading={loading}
          onClick={() => onNavigate('economy')}
          tone="neutral"
        />
        <KpiTile
          label="SLUTFÖRDA 7D"
          value={kpis ? kpis.completed_7d : emDash}
          subtext="SENASTE 7 DAGARNA"
          loading={loading}
          onClick={() => onNavigate('approvals')}
          tone={kpis && kpis.completed_7d > 0 ? 'ok' : 'neutral'}
        />
        <KpiTile
          label="KONTRAKT /MÅN"
          value={kpis ? formatSek(kpis.contract_total) : emDash}
          subtext={kpis ? `${formatSekFull(kpis.contract_total)} EX MOMS` : '—'}
          loading={loading}
          tone="neutral"
        />
        <KpiTile
          label="POÄNG / VECKA"
          value={kpis ? kpis.week_points.toFixed(1) : emDash}
          subtext={`V.${currentIsoWeek()} SCHEMALAGDA`}
          loading={loading}
          spark={pointsSpark}
          tone="neutral"
        />
      </div>

      {/* ── Main grid: 2/3 + 1/3 ───────────────────────────────────────── */}
      <div className="grid xl:grid-cols-3 gap-3 xl:flex-[3] xl:min-h-0">
        <div className="min-h-[240px] xl:min-h-0 xl:col-span-2">
          <WeekFlowChart weeks={summary?.week_flow ?? []} loading={loading} />
        </div>
        <div className="min-h-[240px] xl:min-h-0">
          <SignalsFeed
            signals={signals}
            loading={loading}
            onNavigate={onNavigate}
            language={language}
          />
        </div>
      </div>

      {/* ── Bottom grid: 3/5 + 2/5 ─────────────────────────────────────── */}
      <div className="grid xl:grid-cols-5 gap-3 xl:flex-[2] xl:min-h-0">
        <div className="min-h-[280px] xl:min-h-0 xl:col-span-3">
          <PortfolioTable
            portfolio={
              portfolio ?? {
                total_count: 0,
                total_contract: 0,
                ok_count: 0,
                top: [],
                exceptions: [],
              }
            }
            loading={loading || !portfolio}
            language={language}
            onNavigate={onNavigate}
          />
        </div>
        <div className="min-h-[280px] xl:min-h-0 xl:col-span-2">
          <CrewPanel
            crew={summary?.crew_load ?? []}
            loading={loading}
            language={language}
          />
        </div>
      </div>

      {/* ── IDAG Rail ─────────────────────────────────────────────────────── */}
      <div className="xl:shrink-0">
        <TodayRail
          jobs={summary?.today.jobs ?? []}
          total={summary?.today.total ?? 0}
          loading={loading}
          language={language}
        />
      </div>
    </div>
  );
}
